import { Box, GlobalStyles, keyframes, styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';
import { CONVERSATION_CARD_BORDER_RADIUS } from 'src/pages/Conversations/constants';

export const TriagePropertyStyles = (): React.JSX.Element => (
  <GlobalStyles
    styles={{
      '@property --triage-angle': {
        syntax: '"<angle>"',
        initialValue: '0deg',
        inherits: 'false',
      },
    }}
  />
);

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 currentColor; }
  70% { box-shadow: 0 0 0 0.5625rem transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
`;

const breathe = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.15); }
`;

const shimmer = keyframes`
  from { background-position: 200% 0; }
  to { background-position: -120% 0; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const borderSweep = keyframes`
  to { --triage-angle: 360deg; }
`;

const land = keyframes`
  0% { opacity: 0; transform: scale(0.85); filter: blur(0.1875rem); }
  60% { filter: blur(0); }
  100% { opacity: 1; transform: scale(1); }
`;

// Gutter avatar. While running it pulses + breathes to signal live work.
export const TriageAvatar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'running',
})<{ running?: boolean }>(({ theme, running }) => ({
  width: '1.75rem',
  height: '1.75rem',
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0,
  marginTop: '0.125rem',
  color: theme.palette.primary.main,
  backgroundColor: alpha(theme.palette.primary.main, 0.16),
  ...(running && { animation: `${pulse} 1.8s ease-in-out infinite` }),
  '& .triage-avatar-icon': {
    fontSize: '0.9375rem',
    width: '0.9375rem',
    height: '0.9375rem',
    ...(running && { animation: `${breathe} 1.8s ease-in-out infinite` }),
  },
}));

// Card body. While running the border sweeps via an animated conic gradient.
export const TriageBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'running',
})<{ running?: boolean }>(({ theme, running }) => {
  const surface = theme.palette.custom.OV.sys.surfaceContainerLow;
  return {
    flex: '0 1 75%',
    maxWidth: '35rem',
    minWidth: 0,
    padding: '0.6875rem 0.875rem 0.75rem',
    borderRadius: CONVERSATION_CARD_BORDER_RADIUS,
    border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
    backgroundColor: surface,
    ...(running && {
      border: '1px solid transparent',
      background: `linear-gradient(${surface}, ${surface}) padding-box, conic-gradient(from var(--triage-angle, 0deg), ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.25)} 55%, ${theme.palette.primary.main}) border-box`,
      animation: `${borderSweep} 2.8s linear infinite`,
    }),
  };
});

export const ShimmerTitle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'running',
})<{ running?: boolean }>(({ theme, running }) => ({
  fontSize: '0.65625rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  ...(running && {
    background: `linear-gradient(90deg, ${theme.palette.custom.OV.sys.onSurfaceVariant}, ${theme.palette.primary.main} 45%, ${theme.palette.custom.OV.sys.onSurfaceVariant})`,
    backgroundSize: '220% 100%',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    animation: `${shimmer} 2.4s linear infinite`,
  }),
}));

export const StatusOrb = styled(Box)(({ theme }) => ({
  width: '0.6875rem',
  height: '0.6875rem',
  borderRadius: '50%',
  flexShrink: 0,
  border: `2px solid ${alpha(theme.palette.primary.main, 0.28)}`,
  borderTopColor: theme.palette.primary.main,
  animation: `${spin} 0.9s linear infinite`,
}));

// Each result chip blur-pops in when it first mounts
export const LandingSlot = styled(Box)({
  display: 'inline-flex',
  minWidth: 0,
  maxWidth: '100%',
  animation: `${land} 0.45s cubic-bezier(0.2, 0.85, 0.3, 1.2) both`,
});

// Color-cycling status text shown while the run is in progress.
export const ShimmerText = styled(Box)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  background: `linear-gradient(90deg, ${theme.palette.custom.OV.sys.onSurfaceVariant}, ${theme.palette.primary.main} 45%, ${theme.palette.custom.OV.sys.onSurfaceVariant})`,
  backgroundSize: '220% 100%',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
  animation: `${shimmer} 2.1s linear infinite`,
}));
